<script setup>
/**
 * CircleAsk: the Mystic's one ask after the recipient's reply in the gift
 * room (2026-10-26, circle consent): "Add <sender> to your circle?" with
 * "Add <sender>" / "Not now". A gift join never adds anyone by itself; this
 * is the only way the room makes a circle. Yes: the friendship both ways
 * (Outer Circle) and one message to the sender, from them. Not now: nothing,
 * and the ask goes. The server asks once per package (`room.circle_ask`).
 */
import { computed, ref } from 'vue'
import { store } from '@/store'
import { careCircle } from '@/api-service'
import { circleAskText } from '@/utils/coven-ring'

const props = defineProps({
  inv: { type: String, default: '' },
  ask: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['answered'])

const t = computed(() => circleAskText((props.ask && props.ask.name) || ''))
const state = ref('asking')   // asking | added | gone
const sending = ref(false)

async function answer (add) {
  if (sending.value) return
  sending.value = true
  let res = null
  try { res = await careCircle(props.inv, store.session_id, add) } catch (e) { res = null }
  sending.value = false
  state.value = add && res && res.added ? 'added' : 'gone'
  emit('answered', { added: state.value === 'added' })
}
</script>

<template>
  <div v-if="state !== 'gone'" class="ca" data-testid="circle-ask">
    <template v-if="state === 'asking'">
      <p class="ca-line" data-testid="circle-ask-line">{{ t.line }}</p>
      <div class="ca-acts">
        <button type="button" class="ca-yes" data-testid="circle-ask-yes" :disabled="sending" @click="answer(true)">{{ t.yes }}</button>
        <button type="button" class="ca-no" data-testid="circle-ask-no" :disabled="sending" @click="answer(false)">{{ t.no }}</button>
      </div>
    </template>
    <p v-else class="ca-line ca-done" data-testid="circle-ask-added"><span aria-hidden="true">✦ </span>{{ t.added }}</p>
  </div>
</template>

<style scoped>
.ca { align-self: flex-start; max-width: min(86%, 520px); margin: 8px 0 10px; padding: 12px 16px 14px; border-radius: 18px 18px 18px 4px;
  background: rgba(31, 16, 54, 0.92); border: 1px solid rgba(195, 169, 255, 0.22); color: #f3eaff; }
.ca-line { margin: 0; font: 500 17px/1.45 'Letter Serif', 'Cormorant Garamond', Georgia, serif; }
.ca-done { color: #ffdfa3; }
.ca-acts { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 10px; }
.ca-yes { min-height: 40px; padding: 0 18px; border-radius: 999px; border: 1px solid rgba(240, 217, 163, 0.55); cursor: pointer;
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #ffdfa3; font: 700 14.5px/1 Inter, system-ui, sans-serif; }
.ca-no { min-height: 40px; padding: 0 14px; border-radius: 999px; border: 1px solid rgba(155, 134, 201, 0.45); background: transparent; cursor: pointer;
  color: #b9a6d6; font: 600 13.5px/1 Inter, system-ui, sans-serif; }
.ca-yes:disabled, .ca-no:disabled { opacity: 0.55; cursor: default; }
.ca-yes:focus-visible, .ca-no:focus-visible { outline: 2px solid #ffdfa3; outline-offset: 2px; }
</style>
